import React from 'react';
import { C } from '../../constants/theme';
import { LTR_TEXT } from '../../i18n/I18nProvider.jsx';
import { surfaceColor, toothSummary } from '../../lib/teeth.js';
import { listSeparator } from '../../i18n/format';

// The hover card.
//
// It answers the question a dentist asks while scanning an arch: "what is this
// tooth, and what is wrong with it?" — WITHOUT clicking, because clicking opens
// an editor and scanning should not risk an edit.
//
// It is an HTML overlay, not SVG <text>: it needs wrapping Persian, mixed
// LTR/RTL runs (an FDI number inside a Persian sentence), and a shadow. And it
// deliberately REPLACES the native <title> tooltips the surfaces used to carry
// — two tooltips for one element is worse than either alone, and the native one
// takes ~1s to appear and cannot be styled or read by a dentist at a glance.
//
// pointerEvents: 'none' is load-bearing: the card follows the cursor and would
// otherwise sit under it, stealing the hover from the tooth and flickering.

const Row = ({ label, children }) => (
  <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginTop: 3 }}>
    <span style={{ fontSize: 10.5, color: '#94a3b8', minWidth: 52 }}>{label}</span>
    <span style={{ fontSize: 11.5, color: '#e2e8f0' }}>{children}</span>
  </div>
);

const Swatch = ({ value }) => (
  <span style={{
    display: 'inline-block', width: 8, height: 8, borderRadius: 2,
    background: surfaceColor(value).fill,
    border: `1px solid ${surfaceColor(value).stroke}`,
    marginInlineEnd: 5,
  }} />
);

export default function ToothTooltip({
  fdi, state, x, y, containerWidth, t, formatDate,
  // What has been DONE to this tooth, and what is still intended — the two
  // chart annotations, as words.
  //
  // Handed in from the maps the chart already holds rather than fetched here:
  // the card follows the cursor across 52 teeth, and a read per hover would be
  // 52 possible round trips for something nobody asked to load.
  treated = null,
  planned = null,
}) {
  if (!fdi) return null;

  const s = toothSummary(fdi, state);

  // Newest first, capped at two: this is a glance, and the drawer's history
  // holds the whole list.
  const allTreated = (treated?.names || []).filter(Boolean).slice().reverse();
  const treatedLines = allTreated.slice(0, 2);
  const hiddenTreated = Math.max(0, allTreated.length - treatedLines.length);
  // Only what is still WAITING. A finished line is already one of the ✓ rows
  // above, and listing it twice would read as two separate things.
  const plannedLines = (planned || [])
    .filter((p) => p && p.status !== 'done' && p.status !== 'rejected')
    .map((p) => p.description)
    .filter(Boolean)
    .slice(0, 2);
  const WIDTH = 210;
  const OFFSET = 16;

  // Flip to the other side of the cursor near the right edge, so the card is
  // never clipped by the panel. Vertical placement is fixed below the cursor:
  // the arch is short enough that the card always has room under it.
  const flip = x + OFFSET + WIDTH > (containerWidth || Infinity);
  const left = flip ? Math.max(4, x - OFFSET - WIDTH) : x + OFFSET;

  return (
    <div
      role="tooltip"
      style={{
        position: 'absolute',
        left, top: y + OFFSET,
        width: WIDTH,
        background: '#0f172a',
        color: '#e2e8f0',
        borderRadius: 8,
        padding: '9px 11px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
        pointerEvents: 'none',
        zIndex: 20,
        // The card is prose, so it follows the READING direction — unlike the
        // arch itself, which is anatomical and pinned to LTR.
        textAlign: 'start',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span style={{ fontSize: 15, fontWeight: 800, color: '#fff' }} {...LTR_TEXT}>{s.fdi}</span>
        <span style={{ fontSize: 12, fontWeight: 600 }}>{t(s.nameKey)}</span>
        {s.isPrimary && (
          <span style={{
            fontSize: 9.5, background: '#334155', color: '#cbd5e1',
            borderRadius: 4, padding: '1px 5px',
          }}>
            {t('chart.primary')}
          </span>
        )}
      </div>

      <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 1 }}>
        {t(s.quadrantKey)}
        {s.universal && <> · {t('chart.universalShort')} <span {...LTR_TEXT}>{s.universal}</span></>}
      </div>

      <div style={{ height: 1, background: '#1e293b', margin: '7px 0' }} />

      {/* An untouched tooth says so plainly. Showing "healthy" for a tooth
          nobody has examined would be a claim the record cannot support. */}
      {!s.isCharted ? (
        <div style={{ fontSize: 11.5, color: '#94a3b8' }}>{t('chart.tooltip.notCharted')}</div>
      ) : (
        <>
          {s.presence !== 'present' && (
            <Row label={t('chart.presence.title')}>{t(`chart.presence.${s.presence}`)}</Row>
          )}

          {s.conditions.length > 0 && (
            <Row label={t('chart.condition.title')}>
              {s.conditions.map((c, i) => (
                <React.Fragment key={c.eventId || i}>
                  {i > 0 && listSeparator()}
                  {t(`chart.conditionName.${c.value}`)}
                  {c.material && (
                    <span style={{ color: '#94a3b8' }}> ({t(`chart.materialName.${c.material}`)})</span>
                  )}
                </React.Fragment>
              ))}
            </Row>
          )}

          {s.surfaces.length > 0 && (
            <Row label={t('chart.surfaces.title')}>
              {s.surfaces.map((sf, i) => (
                <span key={sf.eventId || i} style={{ marginInlineEnd: 7, whiteSpace: 'nowrap' }}>
                  <Swatch value={sf.value} />
                  <span {...LTR_TEXT}>{sf.surface}</span>
                  {' '}
                  <span style={{ color: '#cbd5e1' }}>{t(`chart.surfaceState.${sf.value}`)}</span>
                </span>
              ))}
            </Row>
          )}

          {/* Nothing but a presence of 'present' and no findings: examined and
              sound, which is a real and different answer from "not charted". */}
          {s.presence === 'present' && !s.conditions.length && !s.surfaces.length && (
            <div style={{ fontSize: 11.5, color: '#4ade80' }}>{t('chart.tooltip.sound')}</div>
          )}

          {s.notes.length > 0 && (
            <Row label={t('chart.note.title')}>
              {/* One line only — the card is a glance, not the record. The
                  drawer shows every note. */}
              {s.notes[0].text}
              {s.notes.length > 1 && (
                <span style={{ color: '#94a3b8' }}> +{s.notes.length - 1}</span>
              )}
            </Row>
          )}

          {s.lastRecordedAtMs && formatDate && (
            <div style={{ fontSize: 10, color: '#64748b', marginTop: 6 }}>
              {t('chart.tooltip.lastRecorded')} {formatDate(s.lastRecordedAtMs)}
            </div>
          )}
        </>
      )}

      {/* ── What was DONE, and what is still intended ────────────────────────
          The state above says what the tooth IS; these say what happened to it
          and what is coming. Both were unreachable from the chart: a dentist
          hovering a tooth they had treated an hour earlier saw the state and
          no sign that they were the one who caused it — and on a tooth whose
          treatment changed no state (a جرم‌گیری, one session of a course) the
          card said «ثبت‌نشده».

          Rendered even for an uncharted tooth, which is why it sits OUTSIDE
          the `isCharted` branch above: «کشیدن دندان» leaves nothing on the
          chart and is the most important thing that ever happened to it. */}
      {treatedLines.length > 0 && (
        <div style={{ marginTop: 8, borderTop: '1px solid #334155', paddingTop: 6 }}>
          <div style={{ fontSize: 10.5, color: '#94a3b8', marginBottom: 3 }}>
            {t('chart.tooltip.treated')}
          </div>
          {treatedLines.map((line, i) => (
            <div key={i} style={{ fontSize: 11.5, color: '#5eead4' }}>✓ {line}</div>
          ))}
          {hiddenTreated > 0 && (
            <div style={{ fontSize: 10.5, color: '#64748b' }}>
              {t('chart.tooltip.andMore', { count: hiddenTreated })}
            </div>
          )}
        </div>
      )}

      {plannedLines.length > 0 && (
        <div style={{ marginTop: treatedLines.length ? 4 : 8 }}>
          <div style={{ fontSize: 11.5, color: '#fbbf24' }}>
            {t('chart.tooltip.planned')}{t('common.punct.colon')} {plannedLines.join(' · ')}
          </div>
        </div>
      )}
    </div>
  );
}
